import { useEffect, useMemo, useState } from 'react';
import { Form, ResourceContextProvider, useNotify } from 'react-admin';
import { FieldValues } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Typography } from '@mui/material';
import { typedHttpClient } from '../../../utils/httpClient';
import { ConfigType, DefinitionType } from '../../../lib/constants/typedApiReturnTypes';
import { ClientConfigFormGenerator } from '../../../lib/clientConfigFormGenerator';
import {
  buildOverridesFormRecord,
  CLIENT_CONFIG_KEY_OVERRIDES_FORM_KEY,
  ClientConfigKeyRecord,
  fetchClientConfigKeysByPlatform,
  saveClientConfigDefaults,
} from '../../../lib/clientConfigHelpers';
import authProvider from '../../../utils/authProvider';

export const KioskDefaults = () => {

  const [definition, setDefinition] = useState<DefinitionType | null>(null);
  const [config, setConfig] = useState<ConfigType | undefined>();
  const [keysByPath, setKeysByPath] = useState<Record<string, ClientConfigKeyRecord> | undefined>(undefined);
  const [configReady, setConfigReady] = useState(false);
  const [configLoadError, setConfigLoadError] = useState(false);
  const [keysReady, setKeysReady] = useState(false);
  const [keysLoadedOk, setKeysLoadedOk] = useState(false);
  const [updateTimestamp, setUpdateTimestamp] = useState<number>(0);
  const notify = useNotify();
  const navigate = useNavigate();
  const isDevAdmin = authProvider.isDeveloperAdmin();

  useEffect(() => {

    if (!isDevAdmin) {

      notify('resources.config.text.no_access');
      navigate(-1);

    }

  }, [isDevAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {

    let cancelled = false;
    setConfigReady(false);
    setConfigLoadError(false);
    setKeysReady(false);
    setKeysLoadedOk(false);
    setKeysByPath(undefined);

    typedHttpClient().clientConfig.clientConfigControllerResolveKioskDefaultConfigV0().then((response) => {

      if (cancelled) {

        return;

      }
      setDefinition(response.data.definition ?? null);
      setConfig(response.data.config);
      setConfigReady(true);

    }).catch(() => {

      if (!cancelled) {

        setConfigLoadError(true);
        notify('resources.config.text.defaults_update_error', { type: 'error' });

      }

    });

    fetchClientConfigKeysByPlatform('kiosk', 0)
      .then((keys) => {

        if (!cancelled) {

          setKeysByPath(keys);
          setKeysLoadedOk(true);

        }

      })
      .catch(() => {

        if (!cancelled) {

          setKeysByPath(undefined);
          setKeysLoadedOk(false);
          notify('resources.config.text.defaults_update_error', { type: 'error' });

        }

      })
      .finally(() => {

        if (!cancelled) {

          setKeysReady(true);

        }

      });

    return () => {

      cancelled = true;

    };

  }, [notify, updateTimestamp]);

  const formRecord = useMemo(() => ({

    ...(config || {}),
    ...(keysLoadedOk ? {
      [CLIENT_CONFIG_KEY_OVERRIDES_FORM_KEY]: buildOverridesFormRecord(Object.values(keysByPath || {})),
    } : {}),

  }), [config, keysByPath, keysLoadedOk]);

  const updateConfig = async (values: FieldValues) => {

    if (!definition) {

      return;

    }

    const hasError = await saveClientConfigDefaults(
      'kiosk',
      0,
      definition,
      config as Record<string, unknown> | undefined,
      values as Record<string, unknown>,
      keysLoadedOk ? keysByPath : undefined,
    );
    if (hasError) {

      notify('resources.config.text.defaults_update_error', { type: 'error' });

    } else {

      notify('resources.config.text.defaults_updated');

    }
    setUpdateTimestamp(Date.now());

  };

  return (
    <div>
      <h1>KioskConfig</h1>
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore */}
      <ResourceContextProvider value={'ClientConfig'}>
        {/* noValidate: collapsed groups use display:none; browser HTML5 validation cannot focus required hidden inputs */}
        {configLoadError ? (
          <Typography color="error">
            Failed to load kiosk defaults.
          </Typography>
        ) : configReady && keysReady && (
          <Form noValidate reValidateMode={'onChange'} record={formRecord} mode="onBlur" onSubmit={updateConfig}>
            {definition && (
              <ClientConfigFormGenerator definitions={definition} keysByPath={keysLoadedOk ? keysByPath : undefined} />
            )}
          </Form>
        )}
      </ResourceContextProvider>
    </div>
  );

};
